import { Option } from '@/types';
import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { useEffect, useState } from 'react';

export interface SelectableOption {
  id: string;
  label: string;
  dropdowns: {
    id: string;
    label?: string;
    placeholder?: string;
    options: Option[];
  }[];
}

export interface SelectableOptionsFieldProps {
  id?: string;
  label?: string | React.ReactNode;
  name: string;
  selectableOptions: SelectableOption[];
  error?: string;
  helpers?: FieldHelperProps<any>;
  field?: FieldInputProps<any>;
  meta?: FieldMetaProps<any>;
  isDisabled?: boolean;
  labelFontSize?: string;
  labelMb?: string;
  isRequired?: boolean;
  optionLabelWidth?: string;
  [x: string]: any;
}

interface OptionValue {
  isSelected: boolean;
  dropdownValues: { [key: string]: string };
}

export function SelectableOptionsField(props: SelectableOptionsFieldProps) {
  const {
    id,
    label,
    selectableOptions,
    error,
    helpers,
    field,
    isDisabled,
    labelFontSize,
    labelMb,
    isRequired,
    optionLabelWidth = '240px',
    h = '60px',
    borderColor = 'white',
    border = '3px solid',
    onDark,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: OptionValue }>({});

  useEffect(() => {
    if (field?.value) {
      setSelectedOptions(field.value);
    } else {
      const initialValues: { [key: string]: OptionValue } = {};
      selectableOptions.forEach((option) => {
        initialValues[option.id] = {
          isSelected: false,
          dropdownValues: option.dropdowns.reduce((acc, dropdown) => {
            acc[dropdown.id] = '';
            return acc;
          }, {} as { [key: string]: string }),
        };
      });

      setSelectedOptions(initialValues);

      if (helpers) {
        helpers.setValue(initialValues);
      }
    }
  }, [field?.value, selectableOptions]);

  const handleCheckboxToggle = (optionId: string) => {
    if (isDisabled) return;

    const newSelectedOptions = { ...selectedOptions };
    newSelectedOptions[optionId] = {
      ...newSelectedOptions[optionId],
      isSelected: !newSelectedOptions[optionId]?.isSelected,
    };

    setSelectedOptions(newSelectedOptions);
    updateFormValue(newSelectedOptions);
  };

  const handleDropdownChange = (optionId: string, dropdownId: string, value: string) => {
    if (isDisabled) return;

    const newSelectedOptions = { ...selectedOptions };
    if (!newSelectedOptions[optionId]) {
      newSelectedOptions[optionId] = {
        isSelected: true,
        dropdownValues: {},
      };
    }

    newSelectedOptions[optionId].dropdownValues[dropdownId] = value;

    setSelectedOptions(newSelectedOptions);
    updateFormValue(newSelectedOptions);
  };

  const updateFormValue = (newValue: { [key: string]: OptionValue }) => {
    if (helpers) {
      helpers.setValue(newValue);
    }
  };

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const isError = !!error;

  return (
    <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={labelFontSize || '2xl'}
          lineHeight="normal"
          color={textColorPrimary}
          fontWeight="bold"
          mb={labelMb || 4}
          _hover={{ cursor: 'pointer' }}
        >
          {label}
        </FormLabel>
      )}

      <Flex direction="column" gap={4}>
        {selectableOptions.map((option) => {
          const isSelected = selectedOptions[option.id]?.isSelected;

          return (
            <Flex
              key={option.id}
              direction="row"
              alignItems={{ base: 'flex-start', md: 'center' }}
              minH={h}
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              gap={{ base: 3, md: 0 }}
            >
              <Flex
                alignItems="center"
                minW={optionLabelWidth}
                mt={{ base: 1, md: 0 }}
                cursor={isDisabled ? 'default' : 'pointer'}
                onClick={() => handleCheckboxToggle(option.id)}
              >
                <Checkbox
                  isChecked={isSelected}
                  isDisabled={isDisabled}
                  size="lg"
                  colorScheme="primary"
                  borderRadius="md"
                  required={false}
                  pointerEvents="none"
                />
                <Text fontSize="lg" fontWeight="medium" ml={4}>
                  {option.label}
                </Text>
              </Flex>

              {(isSelected || selectedOptions[option.id]?.isSelected) && (
                <Flex gap={4} flex={1} flexDir={{ base: 'column', md: 'row' }}>
                  {option.dropdowns.map((dropdown) => (
                    <HStack key={dropdown.id} flex={1}>
                      {dropdown.label && (
                        <Text fontSize="lg" fontWeight="bold" mb={1} mr={2}>
                          {dropdown.label}
                        </Text>
                      )}
                      <Select
                        value={selectedOptions[option.id]?.dropdownValues[dropdown.id] || ''}
                        onChange={(e) =>
                          handleDropdownChange(option.id, dropdown.id, e.target.value)
                        }
                        placeholder={dropdown.placeholder || 'Select option'}
                        _placeholder={{ color: 'white' }}
                        color={textColorPrimary}
                        border={border}
                        borderColor={borderColor}
                        borderRadius="10px"
                        h={h}
                        maxH={h}
                        fontSize="lg"
                        isDisabled={isDisabled}
                      >
                        {dropdown.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </HStack>
                  ))}
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>

      {isError && (
        <FormErrorMessage>
          {typeof error === 'string' ? error : 'Invalid selection'}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
